import { BasketContext } from "@database/contexts";
import {
    AddProductInBasketType, Basket,
    BasketItem, Color, ColorSizeEntry, ErrorType, SetBasketType,
    Size,
} from "@utilities/types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useOrganisation } from "./useOrganisation";
import { basketService } from "@database/services";
import { useUser } from "./useUser";
import { VariationTypeEnum } from "@utilities/enums";
import { getAvailableStock } from "@utilities/functions";

export const useBasket = () => {

    const {
        baskets, setBaskets,
        activeBasket, setActiveBasket,
    } = useContext(BasketContext);
    const { organisation } = useOrganisation();
    const { user } = useUser();

    const [getCollabBasketError, setGetCollabBasketError] = useState<ErrorType | null>(null);
    const [getCollabBasketLoading, setGetCollabBasketLoading] = useState<boolean>(false);
    const [saveBasketError, setSaveBasketError] = useState<ErrorType | null>(null);
    const [saveBasketLoading, setSaveBasketLoading] = useState<boolean>(false);
    const [deleteBasketError, setDeleteBasketError] = useState<ErrorType | null>(null);
    const [deleteBasketLoading, setDeleteBasketLoading] = useState<boolean>(false);

    const totalBasket = useMemo(() => (
        activeBasket?.basket.reduce((acc: number, item: BasketItem) => {
            // Utiliser le prix réduit si disponible (campagne/promo), sinon le prix normal
            const effectivePrice = (item.discountedPrice && item.discountedPrice > 0)
                ? item.discountedPrice
                : item.price;
            return acc + (effectivePrice * item.quantity);
        }, 0) || 0
    ), [activeBasket]);

    // Calcul du total sans réduction (pour afficher l'économie)
    const totalWithoutDiscount = useMemo(() => (
        activeBasket?.basket.reduce((acc: number, item: BasketItem) => {
            return acc + (item.price * item.quantity);
        }, 0) || 0
    ), [activeBasket]);

    // Économie totale grâce aux promotions
    const totalSavings = useMemo(() => totalWithoutDiscount - totalBasket, [totalWithoutDiscount, totalBasket]);

    const totalToPay = useMemo(() => totalBasket, [totalBasket]);

    const onGetAllCollabBasket = useCallback(async () => {
        if (!user) return;

        setGetCollabBasketLoading(true);
        const req = await basketService.getCollabBasket(user.token!);

        if (req.code === 200) {
            setBaskets(req.data);
            setGetCollabBasketError(null);
        } else {
            if (req.code === 400 || req.code === 404) {
                setGetCollabBasketError({
                    message: "Paniers non trouvés, veuillez vérifier votre connexion.",
                });
            } else {
                setGetCollabBasketError({
                    message: "Une erreur s'est produite lors de la récupération des paniers.",
                });
            }
        }
        setGetCollabBasketLoading(false);
    }, [user, setBaskets]);

    const onSaveBasketInDatabase = useCallback(async (basketData: Partial<SetBasketType>) => {
        if (!organisation || !user) return;

        setSaveBasketLoading(true);
        const req = await basketService.setBasket({
            ...basketData,
            organisationID: organisation._id,
            collaboratorToken: user.token!,
        });

        if (req.code === 200) {
            setActiveBasket(req.data.basket)
            await onGetAllCollabBasket();
            setSaveBasketError(null);
        } else {
            if (req.code === 400 || req.code === 404) {
                setSaveBasketError({
                    message: "Paniers non trouvés ou données invalides, veuillez vérifier votre connexion.",
                });
            } else {
                setSaveBasketError({
                    message: "Une erreur s'est produite lors de la récupération des paniers.",
                });
            }
        }

        setSaveBasketLoading(false);
    }, [
        organisation, user,
        setActiveBasket, onGetAllCollabBasket,
    ]);

    const onDeleteBasket = useCallback(async (basketId: string) => {
        setDeleteBasketLoading(true);
        const req = await basketService.deleteBasket(basketId);

        if (req.code === 200) {
            await onGetAllCollabBasket();
            setDeleteBasketError(null);
        } else {
            if (req.code === 400 || req.code === 404) {
                setDeleteBasketError({
                    message: "Paniers non trouvés ou données invalides, veuillez vérifier votre connexion.",
                });
            } else {
                setDeleteBasketError({
                    message: "Une erreur s'est produite lors de la récupération des paniers.",
                });
            }
        }

        setDeleteBasketLoading(false);
    }, [onGetAllCollabBasket]);

    // const onAddProductInBasket = useCallback(async(basketElement: Partial<AddProductInBasketType>)=> {
    //     if(!organisation || !user) return;

    //     if(!basketElement.basketID) {
    //         await onSaveBasketInDatabase({
    //             organisationID: organisation._id, 
    //             collaboratorToken: user.token!,
    //             basket: [{
    //                 ...basketElement.productItem,
    //                 quantity: 1,
    //             } as BasketItem],
    //         });
    //         return;
    //     }

    //     let basketTmp = baskets.find((b: Basket) => b._id === basketElement.basketID);
    //     if(!basketTmp) return;

    //     const basketItems = basketTmp.basket.filter((b: BasketItem) => b.product._id === basketElement.productItem?.product?._id);
    //     if(basketItems.length > 0) {
    //         for (let i = 0; i < basketTmp.basket.length; i++) {
    //             const basketItem = basketTmp.basket[i];
    //             if(basketItem.product._id !== basketElement.productItem?.product?._id) continue;

    //             if(basketElement.productItem?.selectedColor && basketElement.productItem?.selectedSize) {
    //                 if(basketItem.selectedColor === basketElement.productItem?.selectedColor && basketItem.selectedSize === basketElement.productItem?.selectedSize) {
    //                     basketTmp.basket[i].quantity = basketTmp.basket[i].quantity + 1;
    //                     break;
    //                 } else {
    //                     basketTmp.basket.push({
    //                         ...basketElement.productItem,
    //                         quantity: 1,
    //                     } as BasketItem);
    //                     break;
    //                 }
    //             }
    //             if(basketItem.selectedColor === basketElement.productItem?.selectedColor) {
    //                 basketTmp.basket[i].quantity = basketTmp.basket[i].quantity + 1;
    //                 break;
    //             }
    //             if(basketItem.selectedSize === basketElement.productItem?.selectedSize) {
    //                 basketTmp.basket[i].quantity = basketTmp.basket[i].quantity + 1;
    //                 break;
    //             }
    //             if(basketItem.product._id === basketElement.productItem?.product?._id) {
    //                 basketTmp.basket[i].quantity = basketTmp.basket[i].quantity + 1;
    //                 break;
    //             }
    //             if(i === basketTmp.basket.length - 1) {
    //                 basketTmp.basket.push({
    //                     ...basketElement.productItem,
    //                     quantity: 1,
    //                 } as BasketItem);
    //             }
    //         }

    //     } else {
    //         basketTmp.basket.push({
    //             ...basketElement.productItem,
    //             quantity: 1,
    //         } as BasketItem);
    //     }

    //     await onSaveBasketInDatabase({
    //         organisationID: organisation._id, 
    //         collaboratorToken: user.token!,
    //         basketID: basketElement.basketID,
    //         basket: basketTmp.basket,
    //     });

    // }, [organisation, user, baskets, onSaveBasketInDatabase]);

    const onAddProductInBasket = useCallback(async (basketElement: Partial<AddProductInBasketType>) => {
        if (!organisation || !user) return;

        const product = basketElement.productItem?.product;
        if (!product) return;

        const selectedColor = basketElement.productItem?.selectedColor;
        const selectedSize = basketElement.productItem?.selectedSize;
        const newQuantity = basketElement.productItem?.quantity || 1;

        // Calculer la quantité disponible en fonction du type de variation
        const availableQuantity = getAvailableStock(
            basketElement.productItem?.product!,
            basketElement.productItem?.selectedColor,
            basketElement.productItem?.selectedSize
        );

        let existingQuantity = 0;
        const existingBasket = baskets.find(b => b._id === basketElement.basketID);
        if (existingBasket) {
            existingBasket.basket.forEach((item) => {
                const sameProduct = item.product._id === product._id;
                const sameColor = item.selectedColor === selectedColor;
                const sameSize = item.selectedSize === selectedSize;

                if (sameProduct && (
                    product.variationType === VariationTypeEnum.NONE ||
                    (product.variationType === VariationTypeEnum.COLOR && sameColor) ||
                    (product.variationType === VariationTypeEnum.SIZE && sameSize) ||
                    (product.variationType === VariationTypeEnum.BOTH && sameColor && sameSize)
                )) {
                    existingQuantity += item.quantity;
                }
            });
        }

        if (!basketElement.replaceQuantity && existingQuantity >= availableQuantity) {
            alert("Stock insuffisant pour ajouter ce produit.");
            return;
        }

        if (!basketElement.basketID) {
            await onSaveBasketInDatabase({
                organisationID: organisation._id,
                collaboratorToken: user.token!,
                basket: [{
                    ...basketElement.productItem,
                    quantity: newQuantity,
                } as BasketItem],
            });
            return;
        }

        let basketTmp = baskets.find((b: Basket) => b._id === basketElement.basketID);
        if (!basketTmp) return;

        const existingItemIndex = basketTmp.basket.findIndex((item) => {
            return (
                item.product._id === product._id &&
                item.selectedColor === selectedColor &&
                item.selectedSize === selectedSize
            );
        });

        if (existingItemIndex !== -1) {
            if (basketElement.replaceQuantity) {
                basketTmp.basket[existingItemIndex].quantity = newQuantity;
            } else {
                basketTmp.basket[existingItemIndex].quantity += 1;
            }
        } else {
            basketTmp.basket.push({
                ...basketElement.productItem,
                quantity: newQuantity,
            } as BasketItem);
        }

        await onSaveBasketInDatabase({
            organisationID: organisation._id,
            collaboratorToken: user.token!,
            basketID: basketElement.basketID,
            basket: basketTmp.basket,
        });
    }, [organisation, user, baskets, onSaveBasketInDatabase]);

    const onRemoveProductInBasket = useCallback(async (
        basketElement: AddProductInBasketType, isRemoveFullElement = false
    ) => {
        if (!organisation || !user) return;

        const basketTmp = baskets.find((b: Basket) => b._id === basketElement.basketID);
        if (!basketTmp) return;

        const basketItems = basketTmp.basket.filter((b: BasketItem) => b.product._id === basketElement.productItem?.product?._id);
        let basketItemIndex = 0;
        if (basketItems.length > 0) {
            for (let i = 0; i < basketTmp.basket.length; i++) {
                const basketItem = basketTmp.basket[i];
                if (basketItem.product._id !== basketElement.productItem?.product?._id) continue;

                if (basketElement.productItem?.selectedColor && basketElement.productItem?.selectedSize) {
                    if (basketItem.selectedColor === basketElement.productItem?.selectedColor && basketItem.selectedSize === basketElement.productItem?.selectedSize) {
                        basketItemIndex = i;
                        break;
                    }
                }
                if (basketItem.selectedColor === basketElement.productItem?.selectedColor) {
                    basketItemIndex = i;
                    break;
                }
                if (basketItem.selectedSize === basketElement.productItem?.selectedSize) {
                    basketItemIndex = i;
                    break;
                }
                if (basketItem.product._id === basketElement.productItem?.product?._id) {
                    basketItemIndex = i;
                    break;
                }
            }
        } else {
            return;
        }

        if (isRemoveFullElement) {
            basketTmp.basket.splice(basketItemIndex, 1);
        } else {
            if (basketTmp.basket[basketItemIndex].quantity > 1) {
                basketTmp.basket[basketItemIndex].quantity = basketTmp.basket[basketItemIndex].quantity - 1;
            } else {
                basketTmp.basket.splice(basketItemIndex, 1);
            }
        }

        if (basketTmp.basket.length === 0) {
            await onDeleteBasket(basketElement.basketID);
            setActiveBasket(null);
        } else {
            await onSaveBasketInDatabase({
                organisationID: organisation._id,
                collaboratorToken: user.token!,
                basketID: basketElement.basketID,
                basket: basketTmp.basket,
            });
        }

    }, [
        organisation, user, baskets,
        onDeleteBasket, setActiveBasket,
        onSaveBasketInDatabase,
    ]);

    return {
        baskets, getCollabBasketError, getCollabBasketLoading,
        saveBasketError, saveBasketLoading,
        deleteBasketError, deleteBasketLoading,
        activeBasket, totalBasket, totalToPay,
        totalWithoutDiscount, totalSavings, // Nouvelles valeurs pour afficher les économies
        setActiveBasket,
        onAddProductInBasket, onRemoveProductInBasket,
        onGetAllCollabBasket, onDeleteBasket,
    };
};
